import React, { useState, useRef, useEffect } from 'react';
import { Briefcase, ChevronDown } from 'lucide-react';
import { searchJobTitles, POPULAR_JOB_TITLES, ALL_JOB_TITLES } from '../../config/jobTitles';
import api from '../../api/api';

/**
 * Job Title Autocomplete Selector
 * @param {String} selectedTitle - Currently selected title
 * @param {Function} onChange - Callback when title changes
 * @param {Boolean} required - Whether title is required
 * @param {String} placeholder - Placeholder text
 */
const TitleSelector = ({ 
  selectedTitle = '', 
  onChange, 
  required = false,
  placeholder = 'e.g. Frontend Developer'
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [filteredTitles, setFilteredTitles] = useState(POPULAR_JOB_TITLES);
  const [customTitles, setCustomTitles] = useState([]);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Load custom titles from backend on mount
  useEffect(() => {
    const loadCustomTitles = async () => {
      try {
        const response = await api.get('/custom-data');
        if (response.data && response.data.customTitles) {
          setCustomTitles(response.data.customTitles);
        }
      } catch (err) {
        // Silently fail - custom titles are optional
        console.log('Could not load custom titles:', err);
      }
    };
    loadCustomTitles();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update  // Filter titles based on search query (including custom titles)
  useEffect(() => {
    if (searchQuery.trim()) {
      // Combine predefined and custom titles
      const allTitles = [...ALL_JOB_TITLES, ...customTitles];
      const lowerQuery = searchQuery.toLowerCase();
      
      // Filter titles that include the query
      const results = allTitles.filter(title => 
        title.toLowerCase().includes(lowerQuery)
      );
      
      // Sort by relevance
      results.sort((a, b) => {
        const aLower = a.toLowerCase();
        const bLower = b.toLowerCase();
        
        // Exact match
        if (aLower === lowerQuery) return -1;
        if (bLower === lowerQuery) return 1;
        
        // Starts with query
        if (aLower.startsWith(lowerQuery) && !bLower.startsWith(lowerQuery)) return -1;
        if (bLower.startsWith(lowerQuery) && !aLower.startsWith(lowerQuery)) return 1;
        
        // Alphabetical
        return a.localeCompare(b);
      });
      
      setFilteredTitles(results.slice(0, 30));
    } else {
      setFilteredTitles([...POPULAR_JOB_TITLES]);
    }
  }, [searchQuery, customTitles]);

  const selectTitle = async (title) => {
    onChange(title);
    setSearchQuery('');
    setIsDropdownOpen(false);
    inputRef.current?.blur();
    
    // Save custom title to backend if it's not in predefined lists
    const allPredefined = [...ALL_JOB_TITLES];
    const isCustom = !allPredefined.some(t => t.toLowerCase() === title.toLowerCase());
    
    if (isCustom && title.trim()) {
      try {
        await api.post('/custom-data', {
          type: 'title',
          value: title.trim()
        });
        // Add to local custom titles if not already there
        if (!customTitles.some(t => t.toLowerCase() === title.toLowerCase())) {
          setCustomTitles(prev => [...prev, title.trim()].sort());
        }
      } catch (err) {
        // Silently fail - saving custom title is not critical
        console.log('Could not save custom title:', err);
      }
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    onChange(value);
    setIsDropdownOpen(true);
  };
  
  const handleBlur = async () => {
    // When user leaves input, save as custom title if it doesn't exist
    const currentValue = selectedTitle || searchQuery;
    if (currentValue && currentValue.trim()) {
      const allPredefined = [...ALL_JOB_TITLES];
      const isCustom = !allPredefined.some(t => t.toLowerCase() === currentValue.toLowerCase());
      
      if (isCustom) {
        try {
          await api.post('/custom-data', {
            type: 'title',
            value: currentValue.trim()
          });
          if (!customTitles.some(t => t.toLowerCase() === currentValue.toLowerCase())) {
            setCustomTitles(prev => [...prev, currentValue.trim()].sort());
          }
        } catch (err) {
          console.log('Could not save custom title:', err);
        }
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsDropdownOpen(false);
    }
  };

  return (
    <div className="w-full relative" ref={dropdownRef}>
      <div className="relative">
        <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600" size={18} />
        <input
          ref={inputRef}
          type="text"
          value={selectedTitle || searchQuery}
          onChange={handleInputChange}
          onFocus={() => {
            setIsDropdownOpen(true);
            if (!searchQuery && !selectedTitle) {
              setFilteredTitles([...POPULAR_JOB_TITLES]);
            }
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          required={required}
          autoComplete="off"
          className="w-full p-4 pl-12 pr-10 bg-white border-b-2 border-black focus:border-yellow-400 focus:outline-none transition-all"
        />
        <ChevronDown 
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" 
          size={18} 
        />
      </div>

      {/* Dropdown */}
      {isDropdownOpen && filteredTitles.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {filteredTitles.map((title, index) => (
            <button
              key={`${title}-${index}`}
              type="button"
              onClick={() => selectTitle(title)}
              className="w-full text-left px-4 py-2.5 hover:bg-gray-100 text-sm transition-colors border-b border-gray-100 last:border-b-0"
            >
              <div className="flex items-center gap-2">
                <Briefcase size={14} className="text-gray-400 flex-shrink-0" />
                <span className="text-gray-800">{title}</span>
              </div>
            </button>
          ))}
        </div>
      )}
      
      {/* Helper text */}
      <p className="text-xs text-gray-500 mt-1">
        Start typing to see suggestions or enter your own title
      </p>
    </div>
  );
};

export default TitleSelector;
